import React, { useState, useEffect, useCallback } from 'react';
import { tiposAPI } from '../services/api';
import PendientesModal from './PendientesModal';

const AdvancedFilters = ({ filters, onFilterChange, showAdvancedFilters = true }) => {
  const [tipos, setTipos] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showPendientesModal, setShowPendientesModal] = useState(false);
  const [searchValue, setSearchValue] = useState(filters.search || '');
  const [isSearching, setIsSearching] = useState(false);

  // Cargar tipos para el filtro
  useEffect(() => {
    const loadTipos = async () => {
      try {
        const response = await tiposAPI.getTipos();
        setTipos(response.data);
      } catch (err) {
        console.error('Error al cargar tipos:', err);
      }
    };
    loadTipos();
  }, []);

  // Actualizar searchValue cuando cambien los filtros externos
  useEffect(() => {
    setSearchValue(filters.search || '');
  }, [filters.search]);

  // Debounce para la búsqueda (más rápido para usuarios PERSONAL)
  const debouncedSearch = useCallback((value) => {
    // Limpiar timeout anterior si existe
    if (window.searchTimeout) {
      clearTimeout(window.searchTimeout);
    }
    
    const timeoutId = setTimeout(() => {
      onFilterChange({
        ...filters,
        search: value
      });
    }, 400); // Aumentado a 400ms para dar más tiempo de escritura
    
    return () => clearTimeout(timeoutId);
  }, [filters, onFilterChange]);

  const handleSearchChange = (value) => {
    setSearchValue(value);
    
    // Limpiar el timeout anterior si existe
    if (window.searchTimeout) {
      clearTimeout(window.searchTimeout);
    }
    
    // Solo mostrar indicador de búsqueda si hay texto
    if (value.length > 0) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
    }
    
    // Crear nuevo timeout para la búsqueda
    window.searchTimeout = setTimeout(() => {
      onFilterChange({
        ...filters,
        search: value
      });
      setIsSearching(false);
    }, 400); // Aumentado a 400ms para dar más tiempo de escritura
  };

  const handleFilterChange = (key, value) => {
    onFilterChange({
      ...filters,
      [key]: value
    });
  };

  const handleClearFilters = () => {
    setSearchValue('');
    onFilterChange({
      search: '',
      tipoId: '',
      estadoPago: '',
      estadoTrabajo: '',
      fechaDesde: '',
      fechaHasta: '',
      montoMin: '',
      montoMax: ''
    });
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.tipoId) count++;
    if (filters.estadoPago) count++;
    if (filters.estadoTrabajo) count++;
    if (filters.fechaDesde) count++;
    if (filters.fechaHasta) count++;
    if (filters.montoMin) count++;
    if (filters.montoMax) count++;
    return count;
  };

  return (
    <div className="filters">
      {/* Filtros básicos */}
      <div className="filters-row">
        <div className="filter-group">
          <label className="form-label">
            Buscar:
            {isSearching && (
              <span className="search-indicator">
                <i className="fas fa-spinner fa-spin ml-1"></i>
              </span>
            )}
          </label>
          <div className="search-input-container">
            <input
              type="text"
              className={`form-control ${isSearching ? 'searching' : ''}`}
              placeholder={showAdvancedFilters 
                ? "Buscar por cliente, número o celular..." 
                : "Buscar expedientes por cliente o número..."
              }
              value={searchValue}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            {searchValue && (
              <button
                className="btn btn-sm btn-outline-secondary clear-search"
                onClick={() => handleSearchChange('')}
                title="Limpiar búsqueda"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>
        
        {showAdvancedFilters && (
          <>
            <div className="filter-group">
              <label className="form-label">Tipo:</label>
              <select
                className="form-control"
                value={filters.tipoId || ''}
                onChange={(e) => handleFilterChange('tipoId', e.target.value)}
              >
                <option value="">Todos los tipos</option>
                {tipos.map((tipo) => (
                  <option key={tipo.id} value={tipo.id}>
                    {tipo.nombre}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label className="form-label">Estado de Pago:</label>
              <select
                className="form-control"
                value={['parcial', 'pendiente'].includes(filters.estadoPago) ? 'debe' : (filters.estadoPago || '')}
                onChange={(e) => handleFilterChange('estadoPago', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="pagado">Pagado (100%)</option>
                <option value="debe">Debe</option>
              </select>
            </div>

            <div className="filter-group">
              <label className="form-label">Estado del Trabajo:</label>
              <select
                className="form-control"
                value={['en_progreso', 'pendiente'].includes(filters.estadoTrabajo) ? 'incompleto' : (filters.estadoTrabajo || '')}
                onChange={(e) => handleFilterChange('estadoTrabajo', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="completado">Completado (100%)</option>
                <option value="incompleto">Incompleto</option>
              </select>
            </div>

            <div className="filter-group">
              <button
                className="btn btn-secondary"
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                {showAdvanced ? 'Ocultar' : 'Mostrar'} Filtros Avanzados
              </button>
            </div>
          </>
        )}
      </div>

      {/* Filtros avanzados */}
      {showAdvanced && (
        <div className="filters-row">
          <div className="filter-group">
            <label className="form-label">Fecha Desde:</label>
            <input
              type="date"
              className="form-control"
              value={filters.fechaDesde || ''}
              onChange={(e) => handleFilterChange('fechaDesde', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label className="form-label">Fecha Hasta:</label>
            <input
              type="date"
              className="form-control"
              value={filters.fechaHasta || ''}
              onChange={(e) => handleFilterChange('fechaHasta', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label className="form-label">Monto Mínimo:</label>
            <input
              type="number"
              className="form-control"
              placeholder="0.00"
              step="0.01"
              min="0"
              value={filters.montoMin || ''}
              onChange={(e) => handleFilterChange('montoMin', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label className="form-label">Monto Máximo:</label>
            <input
              type="number"
              className="form-control"
              placeholder="999999.99"
              step="0.01"
              min="0"
              value={filters.montoMax || ''}
              onChange={(e) => handleFilterChange('montoMax', e.target.value)}
            />
          </div>
        </div>
      )}

      {/* Acciones de filtros */}
      <div className="filters-actions">
        <div className="filters-info">
          {getActiveFiltersCount() > 0 && (
            <span className="filters-count">
              {getActiveFiltersCount()} filtro(s) activo(s)
            </span>
          )}
        </div>
        
        <div className="filters-buttons">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => setShowPendientesModal(true)}
          >
            Pendientes
          </button>
          {getActiveFiltersCount() > 0 && (
            <button
              className="btn btn-outline-secondary"
              onClick={handleClearFilters}
            >
              Limpiar Filtros
            </button>
          )}
        </div>
      </div>

      <PendientesModal
        isOpen={showPendientesModal}
        onClose={() => setShowPendientesModal(false)}
      />
    </div>
  );
};

export default AdvancedFilters;


